<script lang="ts">
  import AIResult from "./AIResult.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import Button from "$components/+blocks/Button/Button.svelte";
  import { Left, StopFilled } from "$components/+blocks/OVIcon";
  import { createEventDispatcher } from "svelte";
  import { v4 as uuidv4 } from "uuid";

  export let currentPrompt: string;

  let dispatch = createEventDispatcher();

  // total questions asked to AI bot
  let questions: { id: number; prompt: string }[] = [{ id: Math.random(), prompt: currentPrompt }];
  let newPrompt: string;

  // state for analysing and processing
  let processing: boolean = true;
  let autoScroll: boolean = true;

  const conversationID = uuidv4();

  $: {
    // reset autoScroll to true whenever a new result is added or modified
    autoScroll = true;
    questions;
  }
</script>

<div class="flex h-full flex-col overflow-hidden">
  <div class="grid grid-cols-3 items-center px-3 pb-1.5 pt-3">
    <IconButton
      variant="tertiary"
      a11yLabel="Go back"
      icon={Left}
      on:click={(e) => {
        e.stopPropagation();
        dispatch("start");
      }}
    />
    <span class="mx-auto text-sm font-semibold text-gray-800 dark:text-gray-100">Ask AI</span>
  </div>

  <div class="flex h-full flex-col overflow-hidden">
    <div
      class="scrollbar-thin flex grow flex-col space-y-[22px] divide-y-[0.5px] divide-black/12 overflow-y-auto overflow-x-hidden pb-2 [mask-composite:_destination-in] [mask-image:_linear-gradient(to_bottom,_transparent_-1%,_black_1%),_linear-gradient(to_top,_transparent_-1%,_black_2%)] dark:divide-white/12"
      on:wheel={() => {
        // disable autoScroll when user scrolls up
        autoScroll = false;
      }}
      on:touchstart={() => {
        // disable autoScroll when user touches the screen
        autoScroll = false;
      }}
    >
      {#each questions as question, index (question.id)}
        <AIResult
          {autoScroll}
          {conversationID}
          prompt={question.prompt}
          processing={index === questions.length - 1 && processing}
          refreshEnabled={index === questions.length - 1}
          on:stop-processing={() => (processing = false)}
          on:refresh={() => {
            processing = true;
            let last = questions.pop();
            if (last) {
              questions = [...questions, { id: Math.random(), prompt: last.prompt }];
            }
          }}
        />
      {/each}
    </div>

    <div class="shrink-0 px-4 pb-4">
      <Separator class="mb-6" />
      <div
        class="flex items-center justify-between gap-8 rounded-[5px] border-[0.5px] border-black/12 bg-black/2 p-2 shadow-md dark:border-white/12 dark:bg-white/2 dark:shadow-xl"
      >
        <input
          bind:value={newPrompt}
          type="text"
          class="grow border-0 bg-transparent text-[13px] font-medium leading-[0] text-gray-500 placeholder:font-normal placeholder:text-neutral-400 focus:outline-none focus:ring-0 dark:text-gray-300 dark:placeholder:text-neutral-600"
          placeholder="Ask a follow-up question..."
          on:keydown={(e) => {
            if (e.key === "Enter") {
              processing = true;
              questions = [...questions, { id: Math.random(), prompt: newPrompt }];
              newPrompt = "";
            }
          }}
        />

        {#if processing}
          <IconButton
            variant="tertiary"
            icon={StopFilled}
            a11yLabel="Stop generating answer"
            on:click={(e) => {
              e.stopPropagation();
              processing = false;
            }}
          />
        {:else}
          <Button
            variant="community"
            on:click={(e) => {
              e.stopPropagation();
              if (!newPrompt) return;
              processing = true;
              questions = [...questions, { id: Math.random(), prompt: newPrompt }];
              newPrompt = "";
            }}
            disabled={!newPrompt}
            label="Ask"
            tooltipLabel={newPrompt ? "Ask" : "Start typing to ask"}
          />
        {/if}
      </div>

      <a
        href="https://www.outverse.com"
        class="mt-[18px] flex justify-center text-xs text-neutral-400 transition-colors hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300"
        target="_blank">Powered by Outverse</a
      >
    </div>
  </div>
</div>
